import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { Stack, Typography, Box, Button } from "@mui/material";

import { AllEnterpriseModule, themeBalham } from "ag-grid-enterprise";
import { AgGridProvider, AgGridReact } from "ag-grid-react";

import _ from "lodash";

import { reportConfig } from "./reportConfig.ts";
import { comparisons, CONSIDER_LINE_TYPES } from "./reportConstants";
import {
  getAccountTotals,
  getExcelColumnName,
  isOrderFound,
  parseField,
} from "./utils";

import { formatNumber } from "../../Helpers/formatNumber.ts";
import { accountDetals } from "../../API/nimbleAPI";
import { enqueueSnackbar } from "notistack";
import { defaultColDef, formulaFuncs, theme } from "./gridConfig.ts";
import { Loader } from "../../Helpers/Helpers.jsx";
import { convertFormula } from "./formulaParser.ts";

const modules = [AllEnterpriseModule];

// 3 -> Heading
// 2 -> Department
// 14 -> Unclassified Dept
// 5 -> Group
// 6, 15 -> COAS
// 4 -> Total

const PARENT_LINE_TYPES = {
  summary: [3],
  detailed: [2, 3, 5, 14],
};
const CHILD_LINE_TYPES = {
  summary: [4, 5],
  detailed: [4, 6, 7, 15],
};

const ViewReport = forwardRef(function ViewReport(
  { initialData, selectionsState, showDataGrid, setGridHasRows, griReadyState },
  ref,
) {
  let formValues = selectionsState.previousSelections;
  let netIncome = selectionsState.netincome;

  const [isGridReady, setIsGridReady] = griReadyState;
  const { layout, values } = initialData || {};

  const gridRef = useRef();

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const [tableData, setTableData] = useState({
    columnDefs: [],
    rowData: [],
  });

  const { columnDefs, rowData } = tableData;

  const layoutType = layout?.LayoutType;
  const genTypeReports = layout?.GenTypeReports[0] || {};
  const displayFormat = layout?.displayFormat || {};
  const watermark = layout?.ReportImageInfo.find((i) => i.ImageType === 1);

  const genType = genTypeReports["GenType"];

  const reportHeaderInfo = genTypeReports.ReportHeaderInfo || {};

  const summaryLines = genTypeReports.SummaryLines || [];
  const detailedLines = genTypeReports.DetailedLines || [];

  const totalSummaryLines = summaryLines.filter((i) => i.LineType === 4);
  const GOPLine = totalSummaryLines.find((i) => i.SummeryFormulaType === 2);
  const incomeBeforeLine = GOPLine
    ? totalSummaryLines.find((i) =>
        isOrderFound(GOPLine.Order, i.AmountFormula),
      )
    : undefined;

  let totalStatLines = incomeBeforeLine
    ? [incomeBeforeLine.LineId, ...getTotalStatLines()]
    : [];

  function getTotalStatLines() {
    let checked = new Set();

    function findLines(order) {
      totalSummaryLines.forEach((i) => {
        if (isOrderFound(order, i.AmountFormula) && !checked.has(i.LineId)) {
          checked.add(i.LineId);
          findLines(i.Order);
        }
      });
    }

    findLines(incomeBeforeLine.Order);

    return Array.from(checked);
  }

  const styleList = new Map(
    genTypeReports.StyleList?.map((i) => [i.StyleId, i.FontModel]),
  );

  const negativeFigures = displayFormat.negative_figures;
  const showFigures = displayFormat.show_figures;
  const showAccounts = displayFormat.show_accounts;
  const showNegativeFigureInRed = displayFormat.show_negative_figures_in_red;

  const hideNoTransactionAccounts = showAccounts?.includes(
    "don'tShowNoTransactionAccounts",
  );

  const hideNullifiedAccounts = showAccounts?.includes(
    "don'tShowNullifiedAccounts",
  );

  const withoutCents = showFigures?.includes("withOutCents");

  const {
    response,
    incomeResponse,
    mappings = {},
    getAccountMaps,
    getAccountMap,
    matchCondition,
  } = reportConfig[genType] || {};

  const mappingKeys = _.keys(mappings);
  const amountKeys = _.map(mappings, "amount");
  const statKeys = _.map(mappings, "stat");

  const netIncomeInfo = netIncome[incomeResponse]?.[0] || [];
  const linesInfo = values?.[response] || [];
  const accountMaps = getAccountMaps?.(linesInfo) || {};
  const accountTotals = getAccountTotals?.(linesInfo, amountKeys, statKeys);

  const soldRoomOrder = detailedLines.find((i) => i.RoomsSold === 1)?.Order;
  const availableRoomOrder = detailedLines.find(
    (i) => i.RoomsAvailable === 1,
  )?.Order;

  const summaryLinesOffset = summaryLines?.length;

  const columnHeaders =
    genTypeReports.ColumnHeaders?.map((item, index) => ({
      ...item,
      parsedFields: parseField(item.DBName),
      styles: buildCSS(styleList.get(item.StyleId), item, {
        applyBG: true,
        alignKey: "AlignText",
      }),
      uniqueField: getExcelColumnName(index),
      accountMap: getAccountMap(accountMaps, item),
    })).map((item, _, columns) => {
      let { DBName, Year = "", Month = "", QtrName = "", parsedFields } = item;

      let { period, base, comparison } = parsedFields;

      let periodComparison = `${period}${comparison}`;
      let periodBase = `${period}${base}`;
      let periodBaseComparison = `${periodBase}${comparison}`;

      let isTotalColumns = DBName.startsWith("Total-");

      const getUniqueField = (s) =>
        columns.find((i) => {
          if (isTotalColumns) {
            return i.DBName === `Total-${s}`;
          }
          return i.DBName === s && matchCondition(i, item);
        })?.uniqueField;

      return {
        ...item,
        relatedFields: {
          actualWithBase: getUniqueField(periodBase),
          actualWithComparison: getUniqueField(periodComparison),
          actualWithBaseComparison: getUniqueField(periodBaseComparison),
          dollarVar: getUniqueField(`${periodBaseComparison}DollarVar`),
        },
        totalFields: isTotalColumns
          ? columns
              .filter((i) => i.DBName === DBName.replace("Total-", ""))
              .map((i) => i.uniqueField)
          : [],
      };
    }) || [];

  window.columnHeaders = columnHeaders;

  const allLines = [
    ...summaryLines.map((i) => ({
      ...i,
      lineDescription: "summary",
      gridOrder: i.Order,
    })),
    ...detailedLines.map((i) => ({
      ...i,
      lineDescription: "detailed",
      gridOrder: i.Order + summaryLinesOffset,
    })),
  ];

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setTableData({ columnDefs: getColumns(), rowData: getRowData() });
  }, [showDataGrid, initialData]);

  useImperativeHandle(ref, () => ({
    getFinalData: (columnKey = "uniqueField") => {
      /**
       *
       * @type {import("ag-grid-enterprise").GridApi} api
       */
      let api = gridRef.current;

      let finalData = [];

      api.forEachNode((node) => {
        let data = structuredClone(node.data);

        if (isLineHidden(data)) return;

        columnHeaders.forEach((head) => {
          if (head.DBName === "COA") {
            data.row[head[columnKey]] = data.HeaderName;
          } else {
            let value = api.getCellValue({
              rowNode: node,
              colKey: `row.${head[columnKey]}`,
            });
            data.row[head[columnKey]] = formatCellValue({
              data,
              value,
              head,
            });
          }
        });

        finalData.push(data);
      });

      return finalData;
    },
  }));

  // API

  // CSS
  function buildCSS(
    styles,
    head = {},
    {
      applyBG = false,
      showNegativeFigureInRed = false,
      alignKey = "AlignData",
      isFlexBox = false,
      pointer = false,
    },
  ) {
    if (!styles) return {};

    let align =
      styles[alignKey] === 0
        ? "left"
        : styles[alignKey] === 1
          ? "center"
          : "right";

    return {
      ...(pointer && { cursor: "pointer" }),
      ...(isFlexBox
        ? {
            justifyContent:
              align === "left"
                ? "flex-start"
                : align === "center"
                  ? "center"
                  : "flex-end",
          }
        : {
            textAlign: align,
          }),

      fontSize: `${styles.FontSize}pt`,
      fontFamily: styles.FontText,
      color: showNegativeFigureInRed ? "red" : styles.FontColor,

      textTransform:
        displayFormat.text_formate === "lower"
          ? "lowercase"
          : displayFormat.text_formate === "upper"
            ? "uppercase"
            : "none",

      ...(applyBG && {
        backgroundColor: styles.BackgroundColour,
      }),

      ...(styles.FontStyleInfo?.Bold && {
        fontWeight: "bold",
      }),

      ...(styles.FontStyleInfo?.Italic && {
        fontStyle: "italic",
      }),

      ...(styles.FontStyleInfo?.UnderLine && {
        textDecoration: "underline",
      }),
    };
  }

  function getCellStyles(data, head, value, alignKey = "AlignData") {
    let baseDBName = head.DBName.replace("Total-", "");
    let styles = data.cellStyles;
    return buildCSS(styles, head, {
      showNegativeFigureInRed: value < 0 && showNegativeFigureInRed,
      alignKey,
      pointer:
        mappingKeys.includes(baseDBName) &&
        [6, 15].includes(data.LineType) &&
        [0, 1, 3].includes(layout.IncomestatementType) &&
        layout["IsRegularReport"],
    });
  }

  function HeaderField({ field }) {
    let styles = {
      ...buildCSS(
        styleList.get(field.StyleId),
        {},
        { applyBG: true, isFlexBox: true },
      ),
      flex: 1,
      px: 2,
      whiteSpace: "nowrap",

      display: "flex",
      alignItems: "center",
    };

    return (
      <>
        <Typography sx={styles}>{field.DisplayName}</Typography>

        {field.DisplayValue && (
          <Typography sx={styles}>{field.DisplayValue}</Typography>
        )}
      </>
    );
  }

  // Line Utils
  function formatCOAName(line) {
    let rules = [
      {
        condition: showAccounts.includes("hideAccountCode"),
        transform: (val) => val.replace(`${line.COACode}. `, ""),
      },
    ];

    return rules.reduce(
      (val, { condition, transform }) => (condition ? transform(val) : val),
      line.HeaderName,
    );
  }

  function transformFormulaRefs(f, column, { rowOffSet }) {
    let result = f.replace(/\d+/g, (match) => {
      match = Number(match);
      if (match === 0) {
        return "";
      }
      return `${column}${Number(match) + rowOffSet}`;
    });

    return `=${convertFormula(result)}`;
  }

  const formulas = ({ line, head, rowIndex }) => {
    rowIndex++; //Incrementing to match formula
    let { lineDescription, ApplySummaryPercFormula } = line;
    let { uniqueField, relatedFields, totalFields } = head;

    let {
      actualWithBase,
      actualWithComparison,
      actualWithBaseComparison,
      dollarVar,
    } = relatedFields;

    let isDetailLine = lineDescription === "detailed";

    const roomRatio = (roomOrder) => {
      if (!roomOrder) return 0;
      return `=DIVIDE(${actualWithBase}${rowIndex}, ${actualWithBase}${roomOrder + summaryLinesOffset})`;
    };

    return {
      // Construct formula
      summaryLine() {
        let amountFormula = line.AmountFormula;
        if (!amountFormula) return 0;

        return transformFormulaRefs(amountFormula, uniqueField, {
          rowOffSet: summaryLinesOffset,
        });
      },

      totalRow() {
        let amountFormula = line.AmountFormula;
        if (!amountFormula) return 0;

        return transformFormulaRefs(amountFormula, uniqueField, {
          rowOffSet: isDetailLine ? summaryLinesOffset : 0,
        });
      },

      percIncome() {
        let percFormula = line.PercFormula;
        if (!percFormula) return 0;

        return transformFormulaRefs(
          percFormula.replace("*100%", ""),
          actualWithBase,
          {
            rowOffSet: isDetailLine ? summaryLinesOffset : 0,
          },
        );
      },

      // Manual Formulas
      dollarVar() {
        let dollarChange = Number(line.DollarChange);
        if (![1, 2].includes(dollarChange)) return 0;

        let actual = actualWithComparison;
        let ly = actualWithBaseComparison;

        let [left, right] = dollarChange === 1 ? [actual, ly] : [ly, actual];

        if (ApplySummaryPercFormula) {
          return `=PERCENTAGE(${left}${rowIndex})-PERCENTAGE(${right}${rowIndex})`;
        }
        return `=ROUND(${left}${rowIndex})-ROUND(${right}${rowIndex})`;
      },

      percVar() {
        if (dollarVar) {
          if (ApplySummaryPercFormula) {
            return `=DECIMAL(DIVIDE(ROUND(${dollarVar}${rowIndex}), ROUND(${actualWithBaseComparison}${rowIndex})))`;
          }
          return `=DIVIDE(ROUND(${dollarVar}${rowIndex}), ROUND(${actualWithBaseComparison}${rowIndex}))`;
        }
        return 0;
      },

      por() {
        return roomRatio(soldRoomOrder);
      },

      par() {
        return roomRatio(availableRoomOrder);
      },

      totalColumn(accountTotal) {
        if (
          genType === 5 &&
          (head.DBName.includes("LY") || head.DBName.includes("Budget"))
        ) {
          return accountTotal || 0;
        }

        let operation = head.DBName.includes("Perc") ? "AVERAGE" : "SUM";

        return `=${operation}(${totalFields
          .map((field) => `${field}${rowIndex}`)
          .join(",")})`;
      },
    };
  };

  // Data processing
  function getRowData() {
    const dividedBy1000 = showFigures?.includes("dividedBy1000");

    let allValues = new Set();

    let data = allLines.map((line, index) => {
      let { LineType, COACode, HeaderName, AccountName, lineDescription } =
        line;

      let isDetailLine = lineDescription === "detailed";
      let isStatLine = LineType === 7;
      let fieldKey = isStatLine ? "stat" : "amount";

      let row;
      if (
        (isDetailLine && LineType === 5) ||
        !CONSIDER_LINE_TYPES.includes(LineType)
      ) {
        row = Object.fromEntries(
          columnHeaders.map((head) => {
            let { DBName, uniqueField } = head;

            let value = "";
            if (DBName === "COA") {
              value = formatCOAName(line);
            }

            return [uniqueField, value];
          }),
        );
      } else {
        let matchedAccountTotal = accountTotals.get(line.DBName);

        row = Object.fromEntries(
          columnHeaders.map((head) => {
            let { DBName, uniqueField, parsedFields, accountMap } = head;
            let baseDBName = DBName.replace("Total-", "");

            let value;

            if (DBName === "COA") {
              value = formatCOAName(line);
            } else {
              const formula = formulas({ line, head, rowIndex: index });

              if (mappingKeys.includes(baseDBName)) {
                let handlers = [
                  {
                    condition: LineType === 4,
                    getValue: () => formula.totalRow(),
                  },
                  {
                    condition: LineType === 5,
                    getValue: () => formula.summaryLine(),
                  },
                  {
                    condition: DBName.startsWith("Total-"),
                    getValue: () => {
                      const accountTotal =
                        matchedAccountTotal?.[fieldKey]?.[
                          mappings[baseDBName]?.[fieldKey]
                        ] ?? 0;

                      return formula.totalColumn(accountTotal);
                    },
                  },
                  {
                    condition: [6, 7, 15].includes(LineType),
                    getValue: () => {
                      let info = accountMap?.get(line.DBName);
                      line.IsNullifiedAccount = info?.IsNullifiedAccount;
                      let value =
                        info?.[fieldKey]?.[mappings[DBName][fieldKey]] ?? 0;
                      if ([6, 15].includes(LineType) && dividedBy1000) {
                        value /= 1000;
                      }
                      value = formatNumber(value, "number", withoutCents);
                      allValues.add(value);
                      return value;
                    },
                  },
                ];
                const handler = handlers.find(({ condition }) => condition);
                value = handler ? handler.getValue() : 0;
              } else {
                let { period, base, comparison } = parsedFields;

                let periodBase = `${period}${base}`;
                let periodBaseComparison = `${periodBase}${comparison}`;

                let formulaMap = {
                  [`${periodBaseComparison}DollarVar`]: formula.dollarVar,
                  [`${periodBaseComparison}PercVar`]: formula.percVar,
                  [`${periodBase}PercIncome`]: formula.percIncome,
                  [`${periodBase}POR`]: formula.por,
                  [`${periodBase}PAR`]: formula.par,
                };
                value = formulaMap[baseDBName]();
              }
            }
            return [uniqueField, value];
          }),
        );
      }

      return {
        LineId: line.LineId,
        ParentId: line.ParentId,
        HeaderName: line.HeaderName,
        LineType: line.LineType,
        IS_StatTotal: line.IS_StatTotal,
        IsNullifiedAccount: line.IsNullifiedAccount,
        Order: line.Order,
        COACode: line.COACode,
        IsNetIncome: line.IsNetIncome,
        DollarChange: Number(line.DollarChange),
        AmountFormula: line.AmountFormula,
        SummaryStatGroup: line.SummaryStatGroup,
        ApplySummaryPercFormula: line.ApplySummaryPercFormula,
        RoomsSold: line.RoomsSold,
        RoomsAvailable: line.RoomsAvailable,

        lineDescription: line.lineDescription,
        gridOrder: line.gridOrder,

        row,
        cellStyles: styleList.get(line.LineId),
      };
    });

    if (allValues.size > 0 && [...allValues].every((value) => value === 0)) {
      setIsGridReady(true);
      setGridHasRows(false);
      return [];
    }

    return data;
  }

  function isLineHidden(line) {
    let {
      LineType,
      lineDescription,
      IsNullifiedAccount,
      noTransaction,
      cellStyles,
    } = line;

    const conditions = {
      displayLine: cellStyles?.DisplayLine === 0,

      nullified:
        lineDescription === "detailed" &&
        hideNullifiedAccounts &&
        IsNullifiedAccount,

      noTransaction: !totalStatLines.includes(line.LineId) && noTransaction,

      layout:
        {
          1: false,
          2: lineDescription !== "detailed",
          3: lineDescription !== "summary",
        }[layoutType] ?? false,
    };

    return Object.values(conditions).some(Boolean);
  }

  async function navigateToTransaction(line, head) {
    let { COACode } = line;
    let { CorpId, parsedFields, DBName, Year, Month, QtrDate } = head;
    let { period, base } = parsedFields;

    if (!CorpId || ![period, `${period}LY`].includes(DBName)) return;

    let reportDates = reportHeaderInfo["HeaderColumn3"];
    let reportDate;

    if (genType === 1) {
      reportDate = reportDates.find((i) => i.DisplayName === period);
    } else {
      reportDate = reportDates[0];
    }

    let [FromDate, ToDate] = [reportDate.FromDate, reportDate.ToDate].map(
      (s) => new Date(s),
    );

    let reportDateInfo = {
      from: {
        year: FromDate.getFullYear(),
        month: FromDate.getMonth(),
        day: FromDate.getDate(),
      },
      to: {
        year: ToDate.getFullYear(),
        month: ToDate.getMonth(),
        day: ToDate.getDate(),
      },
    };

    try {
      if ([2, 3, 4, 5].includes(genType)) {
        let monthIndex = { from: null, to: null };

        if (genType === 3) {
          let [from, to] = QtrDate.split("-").map((s) =>
            new Date(`${s.trim().split(" ")[0]} 1, 2000`).getMonth(),
          );
          monthIndex = { from, to };
        } else if (genType === 4) {
          monthIndex = {
            from:
              reportDateInfo.from.year === Year ? reportDateInfo.from.month : 0,
            to: reportDateInfo.to.year === Year ? reportDateInfo.to.month : 11,
          };
        } else {
          monthIndex = { from: Month - 1, to: Month - 1 };
        }

        let fromDay =
          Year === reportDateInfo.from.year &&
          monthIndex.from === reportDateInfo.from.month
            ? reportDateInfo.from.day
            : 1;

        let toDay =
          Year === reportDateInfo.to.year &&
          monthIndex.to === reportDateInfo.to.month
            ? reportDateInfo.to.day
            : 0;

        FromDate = new Date(Year, monthIndex.from, fromDay);

        ToDate = new Date(
          Year,
          toDay === 0 ? monthIndex.to + 1 : monthIndex.to,
          toDay,
        );
      }

      if (base) {
        FromDate.setFullYear(FromDate.getFullYear() - 1);
        ToDate.setFullYear(ToDate.getFullYear() - 1);
      }

      let account = await accountDetals(CorpId, COACode);
      let accountID = account?.id;
      if (!accountID) {
        enqueueSnackbar("Error while getting accountDetails", {
          variant: "error",
        });
        return;
      }

      const formatDate = (date) => {
        return date.toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        });
      };

      let message = {
        accountID,
        corporationID: CorpId,
        fromdate: formatDate(FromDate),
        todate: formatDate(ToDate),
        type: "GeneralreportNav",
        ...(formValues?.profitCenters && {
          StoreID: formValues.profitCenters.join(","),
        }),
      };

      console.log(message);
      window.parent.postMessage(message, "*");
    } catch (e) {
      console.error(e);
    }
  }

  function getScrollRow(line) {
    let { LineType, lineDescription, AmountFormula, Order } = line;
    let isDetailLine = lineDescription === "detailed";

    const getFirstOrder = (s) => Number(s?.match(/\d+/g)?.[0]);

    let firstOrder = getFirstOrder(AmountFormula);

    return allLines.find((i) => {
      if (isDetailLine) {
        if (i.lineDescription === "summary") {
          return getFirstOrder(i.AmountFormula) === Order;
        }
      } else {
        if (i.lineDescription === "detailed") {
          return firstOrder === i.Order;
        }
      }
    });
  }

  function formatCellValue({ head, data, value }) {
    let { LineType, lineDescription, cellStyles } = data;
    let DBName = head.DBName;
    let baseDBName = DBName.replace("Total-", "");

    value = value || 0;

    let isDetailLine = lineDescription === "detailed";

    // 1. Hide rules
    let hideRules = [
      {
        condition:
          DBName.endsWith("DollarVar") && ![1, 2].includes(data.DollarChange),
      },
      {
        condition:
          (isDetailLine && LineType === 5) ||
          !CONSIDER_LINE_TYPES.includes(LineType),
      },
      {
        condition: value === 0 && showFigures.includes("excludeZeroValues"),
      },
      {
        condition:
          (DBName.endsWith("PercIncome") ||
            comparisons.some((v) => DBName.includes(v))) &&
          (data.SummaryStatGroup ||
            data.ApplySummaryPercFormula ||
            LineType === 7),
      },
    ];
    if (hideRules.some((r) => r.condition)) return "";

    // 2. Format rules
    let formatRules = [
      {
        condition:
          baseDBName.includes("Perc") ||
          (mappingKeys.includes(baseDBName) && data.ApplySummaryPercFormula),
        value: formatNumber(value, "percent", withoutCents),
      },
      {
        condition:
          !data.ApplySummaryPercFormula &&
          (data.LineType === 7 ||
            data.RoomsSold === 1 ||
            data.RoomsAvailable === 1 ||
            data.IS_StatTotal),
        value: formatNumber(value, "stat"),
      },
      {
        condition: true, //Default condition to amount
        value: formatNumber(value, "currency", withoutCents),
      },
    ];
    let formatted = formatRules.find((r) => r.condition).value;

    // 3. Negative rules
    let negativeRules = [
      {
        condition: value < 0 && negativeFigures === "withATrailMinus",
        value: `${formatted.replace(/^-/, "")}-`,
      },
      {
        condition: value < 0 && negativeFigures === "parantheses",
        value: `(${formatted.replace(/^-/, "")})`,
      },
      {
        condition: true, //Default return
        value: formatted,
      },
    ];
    return negativeRules.find((r) => r.condition).value;
  }

  function createCol(head, headIndex) {
    /** @type {import("ag-grid-enterprise").ColDef} */
    let col = {
      headerName: head.DisplayName,
      headerTooltip: head.DisplayName,
      meta: head,

      headerClass: `ag-head-text-${head.styles.textAlign}`,
      headerStyle: head.styles,
      field: `row.${head.uniqueField}`,
      ...(head.DBName === "COA"
        ? {
            width: 400,
            pinned: headIndex === 0,
            cellStyle: ({ data, value }) =>
              getCellStyles(data, head, value, "AlignText"),
            filter: import.meta.env.DEV && "agTextColumnFilter",
            tooltipValueGetter: (params) =>
              params.data.LineType === 9 ? null : params.value,
          }
        : {
            cellStyle: ({ data, value }) => getCellStyles(data, head, value),
            width: 200,
            allowFormula: true,
            editable: import.meta.env.DEV,
            valueFormatter: ({ data, value, column }) =>
              formatCellValue({
                data,
                value,
                head: column.colDef.meta,
              }),
          }),
    };
    return col;
  }

  function getColumns() {
    let groupedColumns = _.groupBy(columnHeaders, (item) =>
      item.DBName.startsWith("Total-")
        ? "Total"
        : genType === 6
          ? item.CorpId
          : "NO_CHILD",
    );

    return Object.entries(groupedColumns).flatMap(([key, values]) =>
      key === "NO_CHILD"
        ? values.map(createCol)
        : [
            {
              headerName: key === "Total" ? "Total" : values[0]?.CorpName,
              headerStyle: () => values[0]?.styles,
              children: values.map(createCol),
              headerTooltip: key === "Total" ? "Total" : values[0]?.CorpName,
            },
          ],
    );
  }

  /**
   *
   * @param {import("ag-grid-enterprise").GridApi} api
   * @param {import("ag-grid-enterprise").IRowNode} node
   */
  const getCalculateValues = (api, node) => {
    const calculatedValues = {};

    columnHeaders.forEach((head) => {
      if (head.DBName === "COA") return;

      const value = api.getCellValue({
        rowNode: node,
        colKey: `row.${head.uniqueField}`,
      });

      calculatedValues[head.DBName] = Number(value) || 0;
    });

    return calculatedValues;
  };

  /**
   *
   * @param {import("ag-grid-enterprise").GridApi} api
   */
  function updateNoTransactionRows(api) {
    const hierarchyMap = {
      2: new Map(), //Department
      3: new Map(), //Headings
      5: new Map(), //Groups
      14: new Map(), //Unclassified Department
      CHILD: new Map(), //4, 6, 7, 15
    };

    api.forEachNode((node) => {
      let { LineType, lineDescription, ParentId } = node.data;

      if (CHILD_LINE_TYPES[lineDescription].includes(LineType)) {
        let calculatedValues = getCalculateValues(api, node);

        let noTransaction = Object.values(calculatedValues || {}).every(
          (v) => !v,
        );

        node.data.noTransaction = noTransaction;

        if (LineType === 4) {
          let parent = allLines.find((i) => i.LineId === ParentId);
          if (parent && parent.LineType !== 4) {
            let parentNode = api.getRowNode(parent.gridOrder);
            parentNode.data.noTransaction = noTransaction;

            hierarchyMap[parent.LineType].set(
              parentNode.data.LineId,
              noTransaction,
            );
          }
        }

        hierarchyMap["CHILD"].set(node.data.LineId, noTransaction);
      }
    });

    // Department rows
    api.forEachNode((node) => {
      let { LineId, LineType } = node.data;
      if (LineType === 2) {
        let groupIds = allLines.filter((i) => {
          if (i.LineType === 5 && i.ParentId === LineId) {
            return i.LineId;
          }
        });

        let noTransaction = groupIds.every((id) => hierarchyMap[5].get(id));

        if (noTransaction) {
          node.data.noTransaction = true;
          hierarchyMap[2].set(node.data.LineId, noTransaction);
        }
      }
    });

    // Header rows
    api.forEachNode((node) => {
      let { LineId, LineType } = node.data;
      if (LineType === 3) {
        let childs = allLines.filter((i) => {
          if (
            [2, 4, 5, 6, 7, 15].includes(i.LineType) &&
            i.ParentId === LineId
          ) {
            return i;
          }
        });
        if (!childs.length) return;

        let noTransaction = childs.every((i) => {
          if ([4, 6, 7, 15].includes(i.LineType)) {
            return hierarchyMap["CHILD"].get(i.LineId);
          }
          return hierarchyMap[i.LineType].get(i.LineId);
        });

        if (noTransaction) {
          node.data.noTransaction = true;
          hierarchyMap[3].set(node.data.LineId, noTransaction);
        }
      }
    });

    api.forEachNode((node) => {
      let { LineType, ParentId, lineDescription } = node.data;
      if (
        ![
          ...PARENT_LINE_TYPES[lineDescription],
          ...CHILD_LINE_TYPES[lineDescription],
        ].includes(LineType) &&
        ParentId
      ) {
        let noTransaction = Object.values(hierarchyMap)
          .find((m) => m.has(ParentId))
          ?.get(ParentId);

        if (noTransaction) {
          node.data.noTransaction = true;
        }
      }
    });
  }

  /**
   *
   * @param {import("ag-grid-enterprise").GridApi} api
   */
  function triggerIncomeAlert(api) {
    if (layout.IncomestatementType !== 0 || genType !== 1) return;

    let matchedNode;
    api.forEachNode((node) => {
      if (node.data.IsNetIncome) {
        matchedNode = node;
      }
    });
    if (!matchedNode) return;

    let calculatedValues = getCalculateValues(api, matchedNode);

    if (
      mappingKeys
        .filter((key) => key in calculatedValues)
        .some((key) => {
          return (
            formatNumber(netIncomeInfo?.[mappings[key].income]) !==
            formatNumber(calculatedValues[key])
          );
        })
    ) {
      enqueueSnackbar("Net Income Not Matched");
    }
  }

  const totalColumnsWidth = (columnDefs) =>
    columnDefs.reduce(
      (sum, col) =>
        sum +
        (col.children?.length ? totalColumnsWidth(col.children) : col.width),
      0,
    );

  const formulaFunctions = useMemo(() => {
    return formulaFuncs({ withoutCents });
  }, [withoutCents]);

  const gridWidth = Math.min(
    totalColumnsWidth(columnDefs) + 12 + (import.meta.env.DEV ? 60 : 0),
    windowWidth,
  );

  return (
    <AgGridProvider modules={modules}>
      <Stack
        height={"100%"}
        alignItems={"center"}
        sx={{
          overflowX: "hidden",
          visibility: isGridReady ? "visible" : "hidden",
        }}
      >
        {showDataGrid && initialData && (
          <Stack height={"100%"} gap={0.5}>
            <Stack height={"100%"} gap={1} alignItems={"center"} flexShrink={0}>
              <Stack direction={"row"} minWidth={gridWidth}>
                {Object.values(reportHeaderInfo).map((field, i) => {
                  if (Array.isArray(field)) {
                    return (
                      <Box
                        flex={1}
                        display="grid"
                        gridTemplateColumns="max-content max-content"
                      >
                        {field.map((f, i) => (
                          <HeaderField key={i} field={f} />
                        ))}
                      </Box>
                    );
                  }

                  return <HeaderField field={field} />;
                })}
              </Stack>

              <Box height={"100%"} width={gridWidth}>
                <AgGridReact
                  enableCellTextSelection
                  suppressCellFocus
                  suppressContextMenu
                  tooltipShowDelay={0}
                  tooltipShowMode="whenTruncated"
                  loading={rowData.length === 0}
                  isExternalFilterPresent={() => true}
                  doesExternalFilterPass={(node) => !isLineHidden(node.data)}
                  rowNumbers={import.meta.env.DEV}
                  rowData={rowData}
                  getRowHeight={({ data }) => {
                    if (data.LineType === 10) {
                      return 5;
                    }
                    if (data.LineType === 9) {
                      return 0;
                    }
                  }}
                  onRowDataUpdated={({ api }) => {
                    if (rowData.length === 0) return;

                    triggerIncomeAlert(api);

                    if (hideNoTransactionAccounts) {
                      updateNoTransactionRows(api);
                      api.onFilterChanged();
                    }

                    setGridHasRows(api.getDisplayedRowCount() > 0);
                    gridRef.current = api;
                    setIsGridReady(true);
                  }}
                  getRowId={({ data }) => data.gridOrder}
                  getRowClass={({ data }) =>
                    data.LineType === 9 ? "row-divider" : null
                  }
                  onCellClicked={async ({ data, api, value, colDef }) => {
                    console.log(data);
                    console.log("value", value);

                    let { LineType, lineDescription } = data;
                    let head = colDef.meta;

                    if (
                      [0, 1, 3].includes(layout.IncomestatementType) &&
                      [6, 15].includes(LineType) &&
                      head.DBName !== "COA" &&
                      layout["IsRegularReport"]
                    ) {
                      api.setGridOption("loading", true);
                      await navigateToTransaction(data, head);
                      api.setGridOption("loading", false);
                      return;
                    }

                    if (lineDescription === "detailed") return;
                    if (import.meta.env.DEV && head.DBName !== "COA") return;

                    let targetRow = getScrollRow(data);
                    if (!targetRow) return;

                    let rowNode = api.getRowNode(targetRow.gridOrder);
                    api.ensureNodeVisible(rowNode, "middle");

                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        api.flashCells({
                          rowNodes: [rowNode],
                          flashDuration: 300,
                          fadeDuration: 1500,
                        });
                      });
                    });
                  }}
                  theme={theme}
                  formulaFuncs={formulaFunctions}
                  defaultColDef={defaultColDef}
                  columnDefs={columnDefs}
                />
              </Box>
            </Stack>

            {[1, 2].includes(layout?.IncomestatementType) && (
              <Box pl={1.5}>
                <AgGridReact
                  domLayout="autoHeight"
                  enableCellTextSelection
                  theme={themeBalham.withParams({
                    borderColor: "transparent",
                    fontFamily: "Verdana",
                    fontSize: "10px",
                    autoHeightMinBodyHeight: 0,
                  })}
                  rowData={layout.corporationDetails}
                  columnDefs={[
                    {
                      headerName: "Corporation Name",
                      field: "corporationName",
                    },
                    {
                      headerName: "Legal Name",
                      field: "legalName",
                    },
                    {
                      headerName: "City",
                      field: "City",
                    },
                    {
                      headerName: "State",
                      field: "State",
                    },
                    {
                      headerName: "Address",
                      field: "Address",
                    },
                  ]}
                  defaultColDef={{
                    sortable: false,
                    resizable: false,
                    suppressMovable: true,
                    suppressHeaderMenuButton: true,
                  }}
                  onGridReady={({ api }) => {
                    api.autoSizeAllColumns();
                  }}
                />
              </Box>
            )}
          </Stack>
        )}
      </Stack>
    </AgGridProvider>
  );
});

export default ViewReport;
